import * as React from 'react';
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  Category,
  Customer,
  InteractState,
  Management,
  MenuItem,
  Order,
  Product,
  WorkHour,
} from '@/type';
import { useIntl, type IntlShape } from 'react-intl';
import { useLocalStorage } from './useLocalStorage';
import DataCenter from '@/modules/data/DataCenter';

const interactionStateInitialValue: InteractState = {
  isAuthentication: false,
  setIsAuthentication: () => {},
  translation: {} as IntlShape,
  menus: [],
  products: [],
  categories: [],
  workHours: [],
};

const InteractionStateContext = createContext<InteractState>(
  interactionStateInitialValue
);

interface Props {
  children?: ReactNode;
}

export const InteractionStateContextProvider: FC<Props> = ({
  children,
}: Props) => {
  const [orders, setOrders] = useLocalStorage<Order[]>(DataCenter.CART_KEY, []);
  const [customer, setCustomer] = useLocalStorage<Customer | null>(
    DataCenter.CART_CUSTOMER_KEY,
    null
  );
  const [orderEnable, setOrderEnable] = useState(false);
  const [isAuthentication, setIsAuthentication] = useState<boolean>(false);
  const [management, setManagement] = useState<Management | undefined>();
  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [workHours, setWorkHours] = useState<WorkHour[]>([]);
  const intl = useIntl();

  const values = useMemo(
    () => ({
      isAuthentication,
      setIsAuthentication,
      orders,
      setOrders,
      customer,
      setCustomer,
      orderEnable,
      setOrderEnable,
      management,
      setManagement,
      menus,
      setMenus,
      products,
      setProducts,
      categories,
      setCategories,
      workHours,
      setWorkHours,
      translation: intl,
    }),
    [
      isAuthentication,
      setIsAuthentication,
      orders,
      setOrders,
      customer,
      setCustomer,
      orderEnable,
      setOrderEnable,
      management,
      setManagement,
      menus,
      setMenus,
      products,
      setProducts,
      categories,
      setCategories,
      workHours,
      setWorkHours,
      intl,
    ]
  );
  return (
    <InteractionStateContext.Provider value={values}>
      {children}
    </InteractionStateContext.Provider>
  );
};

export const useInteractionState = (): InteractState => {
  return useContext(InteractionStateContext);
};
